import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import { EssenceBalance } from '@sorare/core/src/components/balance/EssenceBalance';
import { GemsBalance } from '@sorare/core/src/components/commonGame/GemsBalance';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';

import { NewDailyDeals } from 'components/commonCard/BuyPacks/NewDailyDeals';

import { CardPacksGroups } from '../CardPackGroups';
import type { CommonPacksPage_marketRoot } from './__generated__/index.graphql';
import { PackOpeningDialog } from '../BuyPacks/PackOpeningDialog';
import { usePackOpeningDialog } from '../BuyPacks/PackOpeningDialog/usePackOpeningDialog';

type Props = {
  refetch: () => Promise<unknown>;
  marketRoot: CommonPacksPage_marketRoot;
};
export const CommonPacksPage = ({ refetch, marketRoot }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { cardPackGroups, gemsPacks } = marketRoot;
  const { openedCardPack, onPackSelect, onPackOpeningDialogClose } =
    usePackOpeningDialog(cardPackGroups);
  const cardPackGroupsWithoutDaily = marketRoot.cardPackGroups.filter(
    group => !group.dailyDeal
  );

  return (
    <>
      <PullToRefetch refetch={refetch}>
        <Vertical gap={3}>
          <NewDailyDeals
            marketRoot={marketRoot}
            onClickBuy={onPackSelect}
            refetch={refetch}
            balances={
              <Horizontal>
                <EssenceBalance
                  value={currentUser?.commonCardPoints || 0}
                  rarity={Rarity.common}
                />
                <GemsBalance
                  balance={currentUser?.gemsBalances[0]?.amount || 0}
                />
              </Horizontal>
            }
          />
          <Container maxWidth={1500}>
            <CardPacksGroups
              cardPacksGroups={cardPackGroupsWithoutDaily}
              onPackSelect={onPackSelect}
            />
          </Container>
        </Vertical>
      </PullToRefetch>
      {!!openedCardPack && (
        <PackOpeningDialog
          open
          onClose={onPackOpeningDialogClose}
          cardPack={openedCardPack}
          gemsPacks={gemsPacks}
          showPlayerDetails
          starCardPackSlug={
            cardPackGroups
              .find(group =>
                group.cardPacks.some(pack => pack.slug === openedCardPack.slug)
              )
              ?.cardPacks.find(pack => !!pack.rewardedByTask)?.slug
          }
        />
      )}
    </>
  );
};

CommonPacksPage.fragments = {
  marketRoot: gql`
    fragment CommonPacksPage_marketRoot on MarketRoot {
      id
      cardPackGroups(sport: $sport) {
        id
        dailyDeal
        cardPacks {
          slug
          ...PackOpeningDialog_cardPack
        }
        ...CardPacksGroups_cardPackGroup
        ...usePackOpeningDialog_cardPackGroup
      }
      gemsPacks: inGameCurrencyPacks(sport: $sport, currency: COMMON_GEM) {
        id
        ...PackOpeningDialog_inGameCurrencyPack
      }
      ...NewDailyDeals_marketRoot
    }
    ${NewDailyDeals.fragments.marketRoot}
    ${usePackOpeningDialog.fragments.cardPackGroup}
    ${CardPacksGroups.fragments.cardPackGroup}
    ${PackOpeningDialog.fragments.cardPack}
    ${PackOpeningDialog.fragments.inGameCurrencyPack}
  ` as TypedDocumentNode<CommonPacksPage_marketRoot>,
};
